import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';

import DataTable from '../../../sharedModules/dataTable/DataTable/DataTable';
import { IDataTableColumn } from '../../../sharedModules/dataTable/DataTable/model';
import axios from '../../../sharedModules/utils/axios.utils';
import { AuthenticationContext } from '../../../homeModules/auth/contexts/AuthenticationContext';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import FilterSelectInput from '../../../sharedModules/form/components/FilterSelectInput';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';

const columns: IDataTableColumn<any>[] = [
  {
    name: 'Name',
    selector: 'name',
    sortable: true,
  },
  {
    name: 'Email',
    selector: 'email',
    sortable: true,
  },
  {
    name: 'Job Title',
    selector: 'job_title',
    sortable: true,
  },
  {
    name: 'Location',
    selector: 'location',
    sortable: true,
  },
  {
    name: 'Module Access',
    selector: 'module_access',
    sortable: true,
  },
  {
    name: 'Last Login',
    selector: 'last_login_date',
    sortable: true,
  },
  {
    name: 'Join Date',
    selector: 'join_date',
    sortable: true,
    sortFunction: (rowA, rowB) => {
      const dateA = new Date(rowA.join_date);
      const dateB = new Date(rowB.join_date);
      return dateA.getTime() - dateB.getTime();
    },
  },
  {
    name: 'Status',
    selector: 'status',
    sortable: true,
  },
];

const UserListPage: React.FunctionComponent = () => {
  const history = useHistory();
  const { user } = useContext(AuthenticationContext);
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);

  const hasAdminModule = user?.modules?.some((module) => module.value === 'admin' && parseInt(module.tier) > 2);

  const [users, setUsers] = useState<any>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [status, setStatus] = useState<string>('active');
  const [syncing, setSyncing] = useState<boolean>(false);

  const fetchUsers = async (search = '', statusFilter = 'active') => {
    const params: any = {};
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;

    await axios.get('/admin/users', { params }).then((response: any) => {
      setUsers(response.data.result);
    });
  };

  const handleSearch = () => {
    fetchUsers(searchTerm, status);
  };

  const handleStatusChange = (value: string) => {
    setStatus(value);
    fetchUsers(searchTerm, value);
  };

  const handleClear = () => {
    setSearchTerm('');
    setStatus('active');
    fetchUsers('', 'active');
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSyncActiveDirectory = async () => {
    setSyncing(true);
    try {
      await axios.post('/admin/users/sync_active_directory');
      showSuccessSnackBar('Active Directory users synced successfully');
      fetchUsers(searchTerm, status);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to sync Active Directory users';
      showErrorSnackBar(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    // Wait for user to load before checking permissions
    if (!user) {
      return;
    }
    if (!hasAdminModule) {
      history.push('/');
      return;
    }
    fetchUsers('', 'active');
  }, [hasAdminModule, history, user]);

  return (
    <Box style={{ width: '100%' }}>
      <Box>
        <Grid justifyContent="space-between" container spacing={3}>
          <Grid item>
            <Typography variant="h5" component="h5" style={{ marginTop: 5 }}>
              Tudi Users
            </Typography>
          </Grid>
          <Grid item>
            <Button
              onClick={handleSyncActiveDirectory}
              variant="outlined"
              startIcon={syncing ? <CircularProgress size={20} /> : <RefreshIcon />}
              disabled={syncing}
              style={{ marginTop: 5 }}
            >
              {syncing ? 'Syncing...' : 'Sync Active Directory'}
            </Button>
          </Grid>
        </Grid>
        <br />
        <Grid container spacing={1} alignItems="flex-start" onKeyDown={handleKeyPress} style={{ marginBottom: 16 }}>
          <Grid item xs={12} md={3}>
            <TextField
              label="Search by Name or Email"
              variant="standard"
              style={{ width: '100%', marginTop: 8 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Enter first name, last name, or email"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FilterSelectInput
              value={status}
              choices={[
                { label: 'Active', value: 'active' },
                { label: 'Inactive', value: 'inactive' },
                { label: 'All', value: 'all' },
              ]}
              valueChanged={handleStatusChange}
              label="Status"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button onClick={handleSearch} variant="contained" style={{ marginTop: 20 }}>
              Apply
            </Button>
          </Grid>
        </Grid>
        <Card>
          <CardContent>
            <DataTable
              title="Users"
              columns={columns}
              data={users}
              onRowClicked={(row) => history.push(`/admin/user_management/${row.id}/edit`)}
              striped
              dense
              highlightOnHover
              pointerOnHover
              noHeader
              pagination
            />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default UserListPage;
